// src/pages/PostDetail/index.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal, Card, Typography, Button, Tag, Divider, Space, Spin, message, Row, Col, Avatar, Breadcrumb, Tooltip } from 'antd';
import { CalendarOutlined, TagOutlined, EditOutlined, DeleteOutlined, EyeOutlined, UserOutlined, CopyOutlined, ShareAltOutlined, HomeOutlined, FileTextOutlined, LikeOutlined, ClockCircleOutlined, FolderOutlined } from '@ant-design/icons';
import './index.css';

const { Title, Paragraph, Text } = Typography;

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [liked, setLiked] = useState(false);
  const [isAuthor, setIsAuthor] = useState(false);

  // 工具函数
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => message.success('链接已复制'))
      .catch(() => {
        const textArea = document.createElement('textarea');
        textArea.value = window.location.href;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        message.success('链接已复制');
      });
  };

  const handleShare = async () => {
    if (!post) return;
    
    const shareData = { title: post.title, text: post.summary || '分享这篇文章', url: window.location.href };
    
    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        message.success('分享成功');
      } else {
        handleCopyLink();
      }
    } catch (err) {
      if (err.name !== 'AbortError') handleCopyLink();
    }
  };

  const handleLike = () => {
    const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '[]');
    const postId = parseInt(id);
    
    if (liked) {
      const newLikedPosts = likedPosts.filter(item => item !== postId);
      localStorage.setItem('likedPosts', JSON.stringify(newLikedPosts));
      setLiked(false);
      message.success('已取消点赞');
    } else {
      localStorage.setItem('likedPosts', JSON.stringify([...likedPosts, postId]));
      setLiked(true);
      message.success('点赞成功');
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这篇文章吗？此操作不可撤销。',
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        const posts = JSON.parse(localStorage.getItem('blogPosts') || '[]');
        const updatedPosts = posts.filter(p => p.id !== parseInt(id));
        localStorage.setItem('blogPosts', JSON.stringify(updatedPosts));
        
        const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '[]');
        const updatedLikedPosts = likedPosts.filter(postId => postId !== parseInt(id));
        localStorage.setItem('likedPosts', JSON.stringify(updatedLikedPosts));
        
        message.success('文章已删除');
        navigate('/');
      }
    });
  };

  const handleEdit = () => {
    if (!isAuthor) return message.warning('只有作者可以编辑文章');
    navigate(`/write/${id}`);
  };

  const handleTagClick = (tag) => navigate(`/archive?search=${tag}`);

  // 加载数据
  useEffect(() => {
    const posts = JSON.parse(localStorage.getItem('blogPosts') || '[]');
    const foundPost = posts.find(p => p.id === parseInt(id));
    
    if (!foundPost) {
      message.error('文章不存在');
      navigate('/');
      setLoading(false);
      return;
    }

    const currentUser = localStorage.getItem('username') || 'admin';
    setIsAuthor(foundPost.author === currentUser);

    // 阅读计数
    const viewedKey = `post_${id}_viewed`;
    const hasViewed = localStorage.getItem(viewedKey);
    let updatedPost = { ...foundPost };

    if (!hasViewed) {
      const updatedPosts = posts.map(p => 
        p.id === parseInt(id) ? { ...p, viewCount: (p.viewCount || 0) + 1 } : p
      );
      localStorage.setItem('blogPosts', JSON.stringify(updatedPosts));
      localStorage.setItem(viewedKey, 'true');
      updatedPost.viewCount = (foundPost.viewCount || 0) + 1;
    }

    setPost(updatedPost);

    // 相关文章 - 修复了混合运算符问题
    const related = posts
      .filter(p => {
        if (p.id === parseInt(id)) return false;
        const commonTags = foundPost.tags?.filter(tag => p.tags?.includes(tag)) || [];
        return commonTags.length > 0 || p.author === foundPost.author;
      })
      .sort((a, b) => {
        const aTags = foundPost.tags?.filter(tag => a.tags?.includes(tag)).length || 0;
        const bTags = foundPost.tags?.filter(tag => b.tags?.includes(tag)).length || 0;
        
        if (aTags !== bTags) return bTags - aTags;
        
        // 修复：避免混合使用 === 和 !==
        const isAuthorA = a.author === foundPost.author;
        const isAuthorB = b.author === foundPost.author;
        if (isAuthorA !== isAuthorB) {
          return isAuthorA ? -1 : 1;
        }
        
        return (b.viewCount || 0) - (a.viewCount || 0);
      })
      .slice(0, 3);

    setRelatedPosts(related);

    // 检查点赞
    const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '[]');
    setLiked(likedPosts.includes(parseInt(id)));

    setLoading(false);
  }, [id, navigate]);

  // 渲染函数
  const formatContent = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    const elements = [];
    let listItems = [];
    
    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      
      if (trimmed === '') {
        if (listItems.length > 0) {
          elements.push(<ul key={`list-${idx}`} className="content-list">{listItems.map((item, i) => <li key={i}>{item}</li>)}</ul>);
          listItems = [];
        }
        elements.push(<br key={`br-${idx}`} />);
        return;
      }
      
      if (/^[*-]\s/.test(trimmed) || /^\d+\.\s/.test(trimmed)) {
        listItems.push(trimmed.replace(/^[*-]\s|^\d+\.\s/, ''));
        return;
      }
      
      if (listItems.length > 0) {
        elements.push(<ul key={`list-before-${idx}`} className="content-list">{listItems.map((item, i) => <li key={i}>{item}</li>)}</ul>);
        listItems = [];
      }
      
      if (trimmed.startsWith('# ')) {
        elements.push(<Title key={`h1-${idx}`} level={2} className="content-heading">{trimmed.substring(2)}</Title>);
      } else if (trimmed.startsWith('## ')) {
        elements.push(<Title key={`h2-${idx}`} level={3} className="content-heading">{trimmed.substring(3)}</Title>);
      } else if (trimmed.startsWith('### ')) {
        elements.push(<Title key={`h3-${idx}`} level={4} className="content-heading">{trimmed.substring(4)}</Title>);
      } else {
        elements.push(<Paragraph key={`p-${idx}`} className="content-paragraph">{trimmed}</Paragraph>);
      }
    });
    
    if (listItems.length > 0) {
      elements.push(<ul key="list-final" className="content-list">{listItems.map((item, i) => <li key={i}>{item}</li>)}</ul>);
    }
    
    return elements;
  };

  if (loading) return <div className="loading-container"><Spin size="large" tip="加载中..." /></div>;
  if (!post) return (
    <div className="not-found-container">
      <Title level={3}>文章不存在</Title>
      <Paragraph type="secondary" style={{ marginBottom: '24px' }}>您访问的文章可能已被删除或不存在</Paragraph>
      <Space>
        <Button type="primary" onClick={() => navigate('/')}>返回首页</Button>
        <Button onClick={() => navigate('/archive')}>浏览归档</Button>
      </Space>
    </div>
  );

  return (
    <div className="post-detail-container">
      <Breadcrumb className="breadcrumb">
        <Breadcrumb.Item onClick={() => navigate('/')}><HomeOutlined /><span className="breadcrumb-link">首页</span></Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => navigate('/archive')}><span className="breadcrumb-link">归档</span></Breadcrumb.Item>
        <Breadcrumb.Item><span className="breadcrumb-current">{post.title}</span></Breadcrumb.Item>
      </Breadcrumb>

      <div className="main-content">
        <Row gutter={32}>
          <Col xs={24} lg={17}>
            <Card className="post-main-card">
              <Title level={1} className="post-title">{post.title}</Title>
              
              <div className="post-meta-section">
                <Space size="large" wrap>
                  <div className="meta-item">
                    <Avatar size="default" icon={<UserOutlined />} className="author-avatar" />
                    <div className="author-info">
                      <Text strong className="author-name">{post.author || '管理员'}</Text>
                      {isAuthor && <Tag color="blue" size="small" style={{ marginLeft: '8px' }}>作者</Tag>}
                    </div>
                  </div>
                  
                  <div className="meta-item">
                    <CalendarOutlined className="meta-icon" />
                    <Text type="secondary">{formatDate(post.createdAt)}</Text>
                    {post.updatedAt !== post.createdAt && (
                      <Tooltip title={`更新于 ${formatDate(post.updatedAt)}`}>
                        <Tag color="green" size="small" style={{ marginLeft: '8px' }}>已更新</Tag>
                      </Tooltip>
                    )}
                  </div>
                  
                  <div className="meta-item">
                    <EyeOutlined className="meta-icon" />
                    <Text strong style={{ color: '#1890ff' }}>{post.viewCount || 0}</Text>
                    <Text type="secondary">次阅读</Text>
                  </div>
                </Space>
              </div>
              
              {post.tags?.length > 0 && (
                <div className="tags-section">
                  <div className="tags-header"><TagOutlined /><Text type="secondary" style={{ marginLeft: '8px' }}>标签</Text></div>
                  <Space wrap className="tags-container">
                    {post.tags.map(tag => <Tag key={tag} color="blue" className="post-tag" onClick={() => handleTagClick(tag)}>{tag}</Tag>)}
                  </Space>
                </div>
              )}
              
              <Divider />
              
              {post.summary && (
                <div className="summary-section">
                  <div className="summary-header"><FileTextOutlined style={{ color: '#1890ff' }} /><Text strong style={{ marginLeft: '8px' }}>文章摘要</Text></div>
                  <Paragraph className="post-summary">{post.summary}</Paragraph>
                </div>
              )}
              
              <Divider />
              
              <div className="content-section">
                <div className="content-header"><Text strong style={{ fontSize: '18px' }}>正文内容</Text></div>
                <div className="post-content">{formatContent(post.content)}</div>
              </div>
              
              <div className="action-buttons-section">
                <Row justify="space-between" align="middle">
                  <Col><Space>
                    <Button type={liked ? 'primary' : 'default'} icon={<LikeOutlined />} onClick={handleLike} className={liked ? 'liked-button' : ''}>{liked ? '已点赞' : '点赞'}</Button>
                    <Button icon={<CopyOutlined />} onClick={handleCopyLink}>复制链接</Button>
                    <Button icon={<ShareAltOutlined />} onClick={handleShare}>分享</Button>
                  </Space></Col>
                </Row>
              </div>
            </Card>
            
            {relatedPosts.length > 0 && (
              <Card className="related-posts-card" title={<Space><FolderOutlined /><Text strong>相关文章</Text></Space>}>
                <Row gutter={16}>
                  {relatedPosts.map(item => (
                    <Col xs={24} md={8} key={item.id}>
                      <Card hoverable className="related-post-item" onClick={() => navigate(`/post/${item.id}`)}>
                        <div className="related-post-content">
                          <Text strong ellipsis={{ rows: 2 }} className="related-title">{item.title}</Text>
                          <div className="related-meta">
                            <Space size="small">
                              <Text type="secondary" style={{ fontSize: '12px' }}><EyeOutlined /> {item.viewCount || 0}</Text>
                              <Text type="secondary" style={{ fontSize: '12px' }}><ClockCircleOutlined /> {item.createdAt.split(' ')[0]}</Text>
                            </Space>
                          </div>
                          {item.summary && <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ fontSize: '13px', marginTop: '8px' }}>{item.summary}</Paragraph>}
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card>
            )}
          </Col>
          
          <Col xs={24} lg={7}>
            <div className="sidebar">
              <Card className="info-card" title={<Space><FileTextOutlined /><Text strong>文章信息</Text></Space>} size="small">
                {[
                  ['文章ID', `#${post.id}`],
                  ['创建时间', formatDate(post.createdAt)],
                  ['更新时间', formatDate(post.updatedAt)],
                  ['阅读次数', <div className="view-count"><EyeOutlined style={{ color: '#1890ff', marginRight: '4px' }} /><Text strong style={{ color: '#1890ff' }}>{post.viewCount || 0}</Text></div>],
                  ['标签数量', post.tags?.length || 0]
                ].map(([label, value], idx) => (
                  <React.Fragment key={idx}>
                    <div className="info-item"><Text type="secondary">{label}</Text>{value}</div>
                    {idx < 4 && <Divider className="info-divider" />}
                  </React.Fragment>
                ))}
              </Card>
              
              <Card className="action-card" title={<Space><FileTextOutlined /><Text strong>快速操作</Text></Space>} size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button icon={<EditOutlined />} onClick={handleEdit} disabled={!isAuthor} block>{isAuthor ? '编辑文章' : '仅作者可编辑'}</Button>
                  <Button icon={<DeleteOutlined />} onClick={handleDelete} disabled={!isAuthor} danger block>{isAuthor ? '删除文章' : '仅作者可删除'}</Button>
                  <Button icon={<CopyOutlined />} onClick={handleCopyLink} block>复制链接</Button>
                  <Button icon={<ShareAltOutlined />} onClick={handleShare} block>分享文章</Button>
                </Space>
              </Card>
              
              <Card className="author-card" title={<Space><UserOutlined /><Text strong>作者信息</Text></Space>} size="small">
                <div className="author-details">
                  <Avatar size={64} icon={<UserOutlined />} className="author-avatar-large" />
                  <div className="author-name-section">
                    <Text strong style={{ fontSize: '16px' }}>{post.author || '管理员'}</Text>
                    {isAuthor && <Tag color="blue" style={{ marginTop: '4px' }}>当前登录</Tag>}
                  </div>
                </div>
                <Button type="link" block onClick={() => navigate(`/archive?author=${post.author}`)} style={{ marginTop: '12px' }}>查看该作者所有文章 →</Button>
              </Card>
              
              <Button type="dashed" block className="back-to-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>↑ 回到顶部</Button>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default PostDetail;